// channelMentions.js

import { getPersonKey, getPersonName } from 'utilities/PersonIdentity';

export const MENTIONED_PERSON_KEY_PREFIX = 'mp:';

const normalizeKey = (value) => String(value || '').trim();

export function isMentionedPersonMessageKey(value) {
  const key = normalizeKey(value);
  return key.startsWith(MENTIONED_PERSON_KEY_PREFIX)
    && key.length > MENTIONED_PERSON_KEY_PREFIX.length;
}

export function toMentionedPersonMessageKey(mentionedPersonKey) {
  const key = normalizeKey(mentionedPersonKey);
  if (!key) return '';
  if (isMentionedPersonMessageKey(key)) return key;
  return `${MENTIONED_PERSON_KEY_PREFIX}${key}`;
}

export function fromMentionedPersonMessageKey(messageKey) {
  const key = normalizeKey(messageKey);
  if (!isMentionedPersonMessageKey(key)) return '';
  return key.slice(MENTIONED_PERSON_KEY_PREFIX.length);
}

const compareByName = (a, b) => (
  String(a?.name || '').localeCompare(String(b?.name || ''), undefined, { sensitivity: 'base' })
);

/**
 * Returns active trigger-query details when the cursor is inside a token
 * started by the given character (e.g. `@` or `#`).
 *
 * @param {string} text - Composer text.
 * @param {number} cursorPosition - Textarea selection start.
 * @param {string} triggerChar - Single-character trigger.
 * @returns {{ query: string, start: number, end: number }|null}
 */
export function getActiveTriggerQuery(text, cursorPosition, triggerChar = '@') {
  const trigger = String(triggerChar || '').slice(0, 1);
  if (!trigger) return null;

  const value = String(text || '');
  const cursor = Number.isFinite(cursorPosition) ? cursorPosition : value.length;
  const beforeCursor = value.slice(0, cursor);
  const triggerIndex = beforeCursor.lastIndexOf(trigger);

  if (triggerIndex < 0) return null;

  const charBeforeTrigger = triggerIndex > 0 ? beforeCursor[triggerIndex - 1] : '';
  if (charBeforeTrigger && !/\s/.test(charBeforeTrigger)) return null;

  const query = beforeCursor.slice(triggerIndex + 1);
  if (/\s/.test(query)) return null;

  return {
    query,
    start: triggerIndex,
    end: cursor,
  };
}

/**
 * Returns active @-mention query details when the cursor is inside a mention token.
 *
 * @param {string} text - Composer text.
 * @param {number} cursorPosition - Textarea selection start.
 * @returns {{ query: string, start: number, end: number }|null}
 */
export function getActiveMentionQuery(text, cursorPosition) {
  return getActiveTriggerQuery(text, cursorPosition, '@');
}

/**
 * Builds mention autocomplete candidates for a channel composer.
 *
 * @param {object} options - Candidate options.
 * @returns {Array<{ userKey: string, name: string, avatarNode: object, inChannel: boolean, kind?: string }>}
 */
export function buildMentionCandidates({
  channelMembers = [],
  baseMembers = [],
  mentionedPeople = [],
  isConversation = false,
  includeInviteCandidates = false,
  includeAllBaseMembers = false,
} = {}) {
  const seen = new Set();
  const inChannel = [];
  const notInChannel = [];
  const mentioned = [];

  const appendCandidate = (member, inChannelMember) => {
    const userKey = getPersonKey(member);
    if (!userKey || seen.has(userKey)) return;

    seen.add(userKey);
    const row = {
      userKey,
      name: getPersonName(member, 'Member'),
      avatarNode: member?.user || member,
      inChannel: inChannelMember,
      kind: 'member',
    };

    if (inChannelMember) {
      inChannel.push(row);
    } else {
      notInChannel.push(row);
    }
  };

  (Array.isArray(channelMembers) ? channelMembers : []).forEach((member) => {
    appendCandidate(member, true);
  });

  if (includeAllBaseMembers) {
    (Array.isArray(baseMembers) ? baseMembers : []).forEach((member) => {
      appendCandidate(member, true);
    });

    const spaceCandidate = {
      userKey: '__space__',
      name: 'base',
      avatarNode: null,
      inChannel: true,
      isBroadcastHere: true,
      isBroadcastSpace: true,
      kind: 'broadcast',
    };
    if (!seen.has(spaceCandidate.userKey)) {
      seen.add(spaceCandidate.userKey);
      inChannel.unshift(spaceCandidate);
    }
  } else if (isConversation) {
    // conversation: channel members only below
  } else if (includeInviteCandidates) {
    (Array.isArray(baseMembers) ? baseMembers : []).forEach((member) => {
      const userKey = getPersonKey(member);
      if (!userKey || seen.has(userKey)) return;
      appendCandidate(member, false);
    });
  }

  (Array.isArray(mentionedPeople) ? mentionedPeople : []).forEach((person) => {
    const personKey = normalizeKey(person?.key);
    const name = normalizeKey(person?.displayName);
    if (!personKey || !name) return;

    const messageKey = toMentionedPersonMessageKey(personKey);
    if (seen.has(messageKey)) return;
    seen.add(messageKey);

    mentioned.push({
      userKey: messageKey,
      name,
      avatarNode: null,
      inChannel: true,
      kind: 'mentioned',
      mentionedPersonKey: personKey,
    });
  });

  if (includeAllBaseMembers) {
    return [...inChannel.sort((a, b) => {
      if (a.isBroadcastHere) return -1;
      if (b.isBroadcastHere) return 1;
      return compareByName(a, b);
    }), ...mentioned.sort(compareByName)];
  }

  if (isConversation) {
    return [...inChannel.sort(compareByName), ...mentioned.sort(compareByName)];
  }

  if (includeInviteCandidates) {
    return [
      ...inChannel.sort(compareByName),
      ...notInChannel.sort(compareByName),
      ...mentioned.sort(compareByName),
    ];
  }

  return [...inChannel.sort(compareByName), ...mentioned.sort(compareByName)];
}

/**
 * Filters mention candidates by query prefix.
 *
 * @param {Array} candidates - Mention candidate rows.
 * @param {string} query - Active mention query without the leading @.
 * @returns {Array}
 */
export function filterMentionCandidates(candidates = [], query = '') {
  const normalizedQuery = String(query || '').trim().toLowerCase();
  const rows = Array.isArray(candidates) ? candidates : [];

  if (!normalizedQuery) return rows.slice(0, 8);

  return rows
    .filter((candidate) => {
      const name = String(candidate?.name || '').toLowerCase();
      const email = String(candidate?.avatarNode?.email || candidate?.email || '').toLowerCase();
      return name.includes(normalizedQuery) || email.includes(normalizedQuery);
    })
    .slice(0, 8);
}

/**
 * Inserts a selected mention into composer text.
 *
 * @param {string} text - Current composer text.
 * @param {number} start - Mention token start index (at @).
 * @param {number} end - Mention token end index (cursor).
 * @param {string} displayName - Selected member display name.
 * @returns {{ text: string, cursor: number, token: { userKey: string, displayName: string } }}
 */
export function insertMentionToken(text, start, end, { userKey, displayName } = {}) {
  const cleanName = String(displayName || '').trim();
  const cleanKey = normalizeKey(userKey);
  const prefix = String(text || '').slice(0, start);
  const suffix = String(text || '').slice(end);
  const mentionText = `@${cleanName} `;
  const nextText = `${prefix}${mentionText}${suffix}`;

  return {
    text: nextText,
    cursor: prefix.length + mentionText.length,
    token: {
      userKey: cleanKey,
      displayName: cleanName,
    },
  };
}

/**
 * Keeps mention tokens that still appear in the composer text.
 *
 * @param {string} text - Composer text.
 * @param {Array<{ userKey: string, displayName: string }>} tokens - Tracked mention tokens.
 * @returns {Array<{ userKey: string, displayName: string }>}
 */
export function syncMentionTokens(text, tokens = []) {
  const value = String(text || '');

  return (Array.isArray(tokens) ? tokens : [])
    .filter((token) => {
      const displayName = String(token?.displayName || '').trim();
      const userKey = normalizeKey(token?.userKey);
      if (!displayName || !userKey) return false;
      return value.includes(`@${displayName}`);
    })
    .reduce((acc, token) => {
      const userKey = normalizeKey(token.userKey);
      if (!userKey || acc.some((row) => row.userKey === userKey)) return acc;
      acc.push({
        userKey,
        displayName: String(token.displayName || '').trim(),
      });
      return acc;
    }, []);
}

/**
 * Resolves unique mention keys from tracked composer tokens.
 *
 * @param {Array<{ userKey: string }>} tokens - Tracked mention tokens.
 * @returns {string[]}
 */
export function resolveMentionKeys(tokens = []) {
  return Array.from(new Set(
    (Array.isArray(tokens) ? tokens : [])
      .map((token) => normalizeKey(token?.userKey))
      .filter(Boolean)
  ));
}

/**
 * Returns mention keys that are not currently in the channel.
 *
 * @param {string[]} mentionKeys - Mention keys from the composer.
 * @param {string[]} channelMemberKeys - Current channel member keys.
 * @returns {string[]}
 */
export function resolveInviteKeys(mentionKeys = [], channelMemberKeys = []) {
  const memberSet = new Set(
    (Array.isArray(channelMemberKeys) ? channelMemberKeys : [])
      .map((key) => normalizeKey(key))
      .filter(Boolean)
  );

  return (Array.isArray(mentionKeys) ? mentionKeys : [])
    .map((key) => normalizeKey(key))
    .filter(Boolean)
    .filter((key) => key !== '__space__' && key !== '__here__')
    .filter((key) => !isMentionedPersonMessageKey(key))
    .filter((key) => !memberSet.has(key));
}

const escapeRegExp = (value) => String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Builds display names for mention highlighting from keys and member profiles.
 *
 * @param {string[]} mentionKeys - Mentioned user keys or mp: registry keys.
 * @param {object} membersByKey - Base member profiles keyed by user key.
 * @param {object} [mentionedPeopleByKey={}] - Mentioned people keyed by registry key or mp: key.
 * @returns {string[]}
 */
export function buildMentionDisplayNames(
  mentionKeys = [],
  membersByKey = {},
  mentionedPeopleByKey = {},
) {
  return (Array.isArray(mentionKeys) ? mentionKeys : [])
    .map((key) => {
      const cleanKey = normalizeKey(key);
      if (!cleanKey) return '';

      if (isMentionedPersonMessageKey(cleanKey)) {
        const personKey = fromMentionedPersonMessageKey(cleanKey);
        const person = mentionedPeopleByKey?.[cleanKey]
          || mentionedPeopleByKey?.[personKey];
        return normalizeKey(person?.displayName || person?.name);
      }

      const profile = membersByKey?.[cleanKey];
      return getPersonName(profile, '');
    })
    .filter(Boolean);
}

/**
 * Splits message text into plain and mention segments for rendering.
 *
 * @param {string} text - Message text.
 * @param {string[]} mentionNames - People display names to highlight (@Name).
 * @param {string[]} [nodeTitles=[]] - Node titles to highlight (#Title).
 * @returns {Array<{ type: 'text'|'mention'|'node_mention', value: string }>}
 */
export function splitMessageTextByMentions(text, mentionNames = [], nodeTitles = []) {
  const value = String(text || '');
  if (!value) return [];

  const names = Array.from(new Set(
    (Array.isArray(mentionNames) ? mentionNames : [])
      .map((name) => String(name || '').trim())
      .filter(Boolean)
  )).sort((a, b) => b.length - a.length);

  const titles = Array.from(new Set(
    (Array.isArray(nodeTitles) ? nodeTitles : [])
      .map((title) => String(title || '').trim())
      .filter(Boolean)
  )).sort((a, b) => b.length - a.length);

  if (!names.length && !titles.length) {
    return [{ type: 'text', value }];
  }

  const parts = [];
  if (names.length) {
    parts.push(`@(${names.map(escapeRegExp).join('|')})`);
  }
  if (titles.length) {
    parts.push(`#(${titles.map(escapeRegExp).join('|')})`);
  }

  const pattern = new RegExp(parts.join('|'), 'g');
  const segments = [];
  let lastIndex = 0;
  let match = pattern.exec(value);

  while (match) {
    if (match.index > lastIndex) {
      segments.push({
        type: 'text',
        value: value.slice(lastIndex, match.index),
      });
    }

    const token = match[0];
    segments.push({
      type: token.startsWith('#') ? 'node_mention' : 'mention',
      value: token,
    });

    lastIndex = match.index + token.length;
    match = pattern.exec(value);
  }

  if (lastIndex < value.length) {
    segments.push({
      type: 'text',
      value: value.slice(lastIndex),
    });
  }

  return segments.length ? segments : [{ type: 'text', value }];
}
